import React, { useState, useEffect, useMemo, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import { toast } from 'react-toastify';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import {
    getDatosParaPruebaConocimientoAPI,
    guardarPruebaConocimientoAPI,
    getTiposCargoAPI,
    getCargosByTipoAPI,
    getEspecialidadesAPI,
    getAllEvaluacionesAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// HU-PR-05 CA-03 a CA-08: "[Campaña] – Prueba de conocimientos".
//
// Solo aplica a PROMOCIÓN. GDH configura qué evaluación rinde el candidato y al
// guardar se lo deriva al Módulo de Evaluaciones, que le manda el link por correo
// y se encarga del resto del flujo. Acá no se rinde nada.
//
// Los datos personales van precargados y bloqueados (R-02): salen de lo que
// Intranet ya tiene del colaborador. Si GDH pudiera editarlos, se derivaría a una
// persona con los datos de otra — el Módulo de Evaluaciones crea su registro a
// partir de lo que recibe.
// ──────────────────────────────────────────────────────────────────────────

// Los combos de Datos/* devuelven {Value, Text}; el de evaluaciones viene del
// Módulo de Evaluaciones con otra forma. Se normaliza todo a {value, label}.
const mapCombo = (item) => ({
    value: item.Value ?? item.value ?? item.Id ?? item.id,
    label: item.Text ?? item.text ?? item.Nombre ?? item.nombre ?? '',
});

// El Módulo de Evaluaciones envuelve el arreglo a distinta profundidad según el
// endpoint: se lo ha visto en data.Data y en data.data.Data. Por eso se busca el
// primer arreglo en vez de asumir una ruta — es el mismo problema que
// ModalCrearPostulante.jsx resuelve con una cadena de `else if`.
const extraerArray = (nodo, profundidad = 0) => {
    if (Array.isArray(nodo)) return nodo;
    if (!nodo || typeof nodo !== 'object' || profundidad > 4) return [];
    for (const clave of ['Data', 'data', 'Items', 'items', 'Results', 'results']) {
        if (nodo[clave] !== undefined) {
            const encontrado = extraerArray(nodo[clave], profundidad + 1);
            if (encontrado.length) return encontrado;
        }
    }
    return [];
};

const ESTADO_POSTULACION_TRUNCADA = [11, 25]; // Vencido, Cancelado

const normalizarEvaluaciones = (payload) => extraerArray(payload)
    .map((ev) => ({
        idEvaluacion: parseInt(ev.idEvaluacion ?? ev.IdEvaluacion ?? ev.id ?? ev.Id, 10),
        nombre: ev.nombreEvaluacion ?? ev.NombreEvaluacion ?? ev.nombre ?? ev.Nombre ?? '',
        idTipoCargo: parseInt(ev.idTipoCargo ?? ev.IdTipoCargo, 10),
        estado: ev.estado ?? ev.Estado,
    }))
    // Mismo criterio que ModalCrearPostulante: no se ofrecen las inactivas.
    .filter((ev) => ev.idEvaluacion && ev.estado !== false);

const PruebaConocimiento = ({ toggleModal, refreshPage, postulante = {} }) => {
    const idPostulante = postulante?.idPostulante;

    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [datos, setDatos] = useState(null);
    const [tiposCargo, setTiposCargo] = useState([]);
    const [cargos, setCargos] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [evaluaciones, setEvaluaciones] = useState([]);

    const [idTipoCargo, setIdTipoCargo] = useState('');
    const [idCargo, setIdCargo] = useState('');
    const [idEspecialidad, setIdEspecialidad] = useState('');
    const [seleccionadas, setSeleccionadas] = useState([]);
    const [esPrecarga, setEsPrecarga] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const precargaRef = useRef(null);

    // Carga inicial: ficha del candidato + catálogos que no dependen de nada.
    useEffect(() => {
        if (!idPostulante) return;
        let cancelado = false;

        (async () => {
            const [fichaResp, tiposResp, especialidadesResp, evaluacionesResp] = await Promise.all([
                executeGet(getDatosParaPruebaConocimientoAPI(idPostulante)),
                executeGet(getTiposCargoAPI()),
                executeGet(getEspecialidadesAPI()),
                executeGet(getAllEvaluacionesAPI('', 1, 200)),
            ]);

            if (cancelado) return;

            const ficha = fichaResp?.data ?? null;
            setDatos(ficha);
            setTiposCargo((tiposResp?.data ?? []).map(mapCombo));
            setEspecialidades((especialidadesResp?.data ?? []).map(mapCombo));
            setEvaluaciones(normalizarEvaluaciones(evaluacionesResp));

            const prueba = ficha?.PruebaVigente;
            if (prueba && prueba.IdTipoCargo != null
                && ESTADO_POSTULACION_TRUNCADA.includes(prueba.IdEstadoPostulacion)) {
                precargaRef.current = {
                    idCargo: prueba.IdCargo != null ? String(prueba.IdCargo) : '',
                    seleccionadas: prueba.EvaluacionesAnteriores ?? [],
                };
                setEsPrecarga(true);
                if (prueba.IdEspecialidad != null) setIdEspecialidad(String(prueba.IdEspecialidad));
               setIdTipoCargo(String(prueba.IdTipoCargo));
            }

            setIsLoading(false);
        })();

        return () => { cancelado = true; };
    }, [idPostulante]);

    useEffect(() => {
        const precarga = precargaRef.current;
        precargaRef.current = null;

        if (!idTipoCargo) {
            setCargos([]);
            setIdCargo('');
            setSeleccionadas([]);
            return;
        }

        let cancelado = false;
        (async () => {
            const resp = await executeGet(getCargosByTipoAPI(idTipoCargo));
            if (cancelado) return;
            setCargos((resp?.data ?? []).map(mapCombo));
        })();

        setIdCargo(precarga?.idCargo ?? '');
        setSeleccionadas(precarga?.seleccionadas ?? []);
        return () => { cancelado = true; };
    }, [idTipoCargo]);

    // CA-05: solo se ofrecen las evaluaciones de la combinación elegida. El filtro
    // por tipo de cargo es el mismo criterio que usa ModalCrearPostulante en el
    // Módulo de Evaluaciones, para no ofrecer acá cosas que allá no existen.
    // La lista recién se ofrece con la combinación completa: el tipo de cargo es lo
    // único por lo que se puede filtrar (es el único dato que traen las evaluaciones),
    // pero cargo y especialidad también viajan al Módulo de Evaluaciones y definen el
    // registro que se crea allá, así que elegir evaluaciones antes de fijarlos permitiría
    // derivar una combinación a medias.
    const seleccionCompleta = Boolean(idTipoCargo && idCargo && idEspecialidad);

    const evaluacionesDisponibles = useMemo(() => {
        if (!seleccionCompleta) return [];
        return evaluaciones.filter((ev) =>
            parseInt(ev.idTipoCargo, 10) === parseInt(idTipoCargo, 10));
    }, [evaluaciones, idTipoCargo, seleccionCompleta]);

    const toggleEvaluacion = (idEvaluacion) => {
        setSeleccionadas((previas) => previas.includes(idEvaluacion)
            ? previas.filter((id) => id !== idEvaluacion)
            : [...previas, idEvaluacion]);
    };

    const handleGuardar = async () => {
        if (!idTipoCargo) return toast.warn('Seleccione el tipo de cargo');
        if (!idCargo) return toast.warn('Seleccione el cargo');
        if (!seleccionadas.length) return toast.warn('Seleccione al menos una evaluación');

        setIsSending(true);
        try {
            await managePost(
                guardarPruebaConocimientoAPI(),
                {
                    IdPostulante: idPostulante,
                    IdTipoCargo: parseInt(idTipoCargo, 10),
                    IdCargo: parseInt(idCargo, 10),
                    IdEspecialidad: idEspecialidad ? parseInt(idEspecialidad, 10) : null,
                    Evaluaciones: seleccionadas,
                },
                () => {
                    toast.success('Prueba de conocimientos derivada exitosamente');
                    refreshPage && refreshPage();
                    toggleModal();
                },
            );
        } finally {
            setIsSending(false);
        }
    };

    const nombreCompleto = datos
        ? [datos.Nombres, datos.ApellidoPaterno, datos.ApellidoMaterno].filter(Boolean).join(' ')
        : '';
    const tituloCampana = datos?.NombreConvocatoria
        ? `${datos.NombreConvocatoria} – Prueba de conocimientos`
        : 'Prueba de conocimientos';

   const pruebaMurioDelOtroLado = datos?.PruebaVigente
        && ESTADO_POSTULACION_TRUNCADA.includes(datos.PruebaVigente.IdEstadoPostulacion);
    const yaDerivado = datos?.PruebaVigente
        && datos.PruebaVigente.IdEstadoPruebaConocimiento == null
        && !datos.PruebaVigente.ObservacionGdh
        && !pruebaMurioDelOtroLado;

    return (
        <GenericModal showModal={toggleModal} titulo={tituloCampana} width="800px">
            <div className="modal-body pt-3">

                {isLoading
                    ? <div className="text-center py-5"><FontAwesomeIcon icon="spinner" className="spinner" /> Cargando…</div>
                    : (
                        <>
                            {/* CA-03: precargados y en solo lectura (R-02) */}
                            <div className="displayFlexWrap">
                                <div className="col-md-6"><InputText label="Colaborador" name="colaborador" value={nombreCompleto} direction="row" isDisabled /></div>
                                <div className="col-md-6"><InputText label="DNI" name="dni" value={datos?.Dni ?? ''} direction="row" isDisabled /></div>
                                <div className="col-md-6"><InputText label="Correo" name="correo" value={datos?.Correo ?? ''} direction="row" isDisabled /></div>
                                <div className="col-md-6"><InputText label="Celular" name="celular" value={datos?.Celular ?? ''} direction="row" isDisabled /></div>
                                <div className="col-md-6"><InputText label="Cargo actual" name="cargoActual" value={datos?.CargoActual ?? ''} direction="row" isDisabled /></div>
                            </div>

                            <hr />

                            {yaDerivado
                                ? (
                                    <AvisoBox>
                                        Este candidato ya tiene una prueba de conocimientos en curso
                                        {datos.PruebaVigente.DesEstadoPostulacion
                                            ? ` (${datos.PruebaVigente.DesEstadoPostulacion})` : ''}.
                                        Para ver su avance o registrar el resultado, use la acción
                                        “Resultado prueba” de la bandeja.
                                    </AvisoBox>
                                )
                                : (
                                    <>
                                        {esPrecarga &&
                                            <PrecargaBox>
                                                La prueba anterior de este candidato quedó sin resultado
                                                {datos?.PruebaVigente?.DesEstadoPostulacion
                                                    ? ` (${datos.PruebaVigente.DesEstadoPostulacion})` : ''}.
                                                Se precargó la misma configuración — puedes enviarla tal
                                                cual o editarla antes de guardar.
                                            </PrecargaBox>
                                        }

                                        {/* CA-04 y CA-05: la cascada */}
                                        <div className="displayFlexWrap">
                                            <div className="col-md-6">
                                                <label className="modal-label">Tipo de cargo</label>
                                                <select className="form-control" value={idTipoCargo}
                                                    onChange={(e) => setIdTipoCargo(e.target.value)}>
                                                    <option value="">Seleccione…</option>
                                                    {tiposCargo.map((tc) => (
                                                        <option key={tc.value} value={tc.value}>{tc.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-md-6">
                                                <label className="modal-label">Cargo</label>
                                                <select className="form-control" value={idCargo} disabled={!idTipoCargo}
                                                    onChange={(e) => setIdCargo(e.target.value)}>
                                                    <option value="">Seleccione…</option>
                                                    {cargos.map((c) => (
                                                        <option key={c.value} value={c.value}>{c.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-md-6">
                                                {/* CA-04: es lo que el candidato va a dictar en el puesto
                                                    al que postula, no su especialidad actual. */}
                                                <label className="modal-label">Especialidad</label>
                                                <select className="form-control" value={idEspecialidad} disabled={!idCargo}
                                                    onChange={(e) => setIdEspecialidad(e.target.value)}>
                                                    <option value="">Seleccione…</option>
                                                    {especialidades.map((esp) => (
                                                        <option key={esp.value} value={esp.value}>{esp.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <hr />

                                        <EvaluacionesBox>
                                            <div className="evaluaciones-titulo">Evaluaciones</div>
                                            {!seleccionCompleta
                                                ? <div className="evaluaciones-vacio">Seleccione tipo de cargo, cargo y especialidad para ver las evaluaciones disponibles.</div>
                                                : evaluacionesDisponibles.length === 0
                                                    ? <div className="evaluaciones-vacio">No hay evaluaciones configuradas para esta combinación.</div>
                                                    : evaluacionesDisponibles.map((ev) => (
                                                        <label className="evaluacion-item" key={ev.idEvaluacion}>
                                                            <input type="checkbox"
                                                                checked={seleccionadas.includes(ev.idEvaluacion)}
                                                                onChange={() => toggleEvaluacion(ev.idEvaluacion)} />
                                                            <span>{ev.nombre}</span>
                                                        </label>
                                                    ))
                                            }
                                        </EvaluacionesBox>
                                    </>
                                )
                            }

                            {/* CA-06: Guardar y Cancelar */}
                            <div className="d-flex justify-content-center mt-4">
                                <button type="button" className="btn btn-secondary mr-3" onClick={toggleModal}>
                                    Cancelar
                                </button>
                                {!yaDerivado && (
                                    <button type="button" disabled={isSending} className="btn generic-button-buscador"
                                        onClick={handleGuardar}>
                                        Guardar {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                                    </button>
                                )}
                            </div>
                        </>
                    )
                }
            </div>
        </GenericModal>
    )
}

export default PruebaConocimiento

const EvaluacionesBox = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;

    .evaluaciones-titulo {
        font-weight: 600;
        margin-bottom: 12px;
    }

    .evaluaciones-vacio {
        color: #6c757d;
        font-size: 0.9rem;
    }

    .evaluacion-item {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        font-weight: normal;
        cursor: pointer;
    }
`;

const AvisoBox = styled.div`
    border: 1px solid #ffe08a;
    background: #fff9e6;
    border-radius: 8px;
    padding: 16px;
    color: #8a6d3b;
`;

const PrecargaBox = styled.div`
    border: 1px solid #b8daff;
    background: #eaf4ff;
    border-radius: 8px;
    padding: 12px 16px;
    color: #1a5a96;
    margin-bottom: 16px;
    font-size: 0.9rem;
`;
